import * as Yup from "yup";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
// @mui
import { LoadingButton } from "@mui/lab";
import {
    Container,
    Grid,
    Card,
    Stack,
    Typography,
    Divider,
    Alert,
} from "@mui/material";
// components
import { useSettingsContext } from "../components/settings";
import { useSnackbar } from "../components/snackbar";
import FormProvider, { RHFTextField } from "../components/hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
    previewMigrateStandalone,
    executeMigrateStandalone,
    MigrateStandalonePreview,
} from "../controllers/user";
import { useState } from "react";

// ----------------------------------------------------------------------

type FormValues = {
    subscriberUserId: string;
    ownerUserId: string;
    groupId?: string;
};

export default function MigrateStandaloneToGroup() {
    const { themeStretch } = useSettingsContext();
    const { enqueueSnackbar } = useSnackbar();

    const defaultValues: FormValues = {
        subscriberUserId: "",
        ownerUserId: "",
        groupId: "",
    };

    const schema = Yup.object().shape({
        subscriberUserId: Yup.string().required("Subscriber userId is required"),
        ownerUserId: Yup.string().required("Target owner userId is required"),
        groupId: Yup.string(),
    });

    const methods = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const {
        handleSubmit,
        formState: { isValid },
    } = methods;

    const [preview, setPreview] = useState<MigrateStandalonePreview | null>(null);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isExecuting, setIsExecuting] = useState(false);

    const onPreview = async (data: FormValues) => {
        setIsPreviewing(true);
        setPreview(null);
        try {
            const result = await previewMigrateStandalone({
                subscriberUserId: data.subscriberUserId.trim(),
                ownerUserId: data.ownerUserId.trim(),
                groupId: data.groupId?.trim() || undefined,
            });
            setPreview(result);
        } catch (error: any) {
            console.error(error);
            enqueueSnackbar(
                error?.response?.data?.message ||
                    "Failed to preview migration",
                { variant: "error" }
            );
        } finally {
            setIsPreviewing(false);
        }
    };

    const onExecute = async () => {
        if (!preview || !preview.canExecute) return;
        setIsExecuting(true);
        try {
            const result = await executeMigrateStandalone({
                subscriberUserId: preview.subscriberUserId,
                ownerUserId: preview.ownerUserId,
                groupId: preview.group.groupId,
            });
            const canceled = result.canceledSubscriptionIds.length
                ? result.canceledSubscriptionIds.join(", ")
                : "none";
            enqueueSnackbar(
                `Migrated into group ${result.groupId}. Canceled subs: ${canceled}`,
                { variant: "success" }
            );
            if (result.cancelFailed.length) {
                enqueueSnackbar(
                    `Some subs failed to cancel — cancel manually in Stripe: ${result.cancelFailed
                        .map((f) => f.id)
                        .join(", ")}`,
                    { variant: "warning", autoHideDuration: 10000 }
                );
            }
            // Re-preview to reflect the new state (now alreadyMigrated).
            await onPreview({
                subscriberUserId: preview.subscriberUserId,
                ownerUserId: preview.ownerUserId,
                groupId: preview.group.groupId,
            });
        } catch (error: any) {
            console.error(error);
            enqueueSnackbar(
                error?.response?.data?.message ||
                    "Failed to execute migration",
                { variant: "error" }
            );
        } finally {
            setIsExecuting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title> Migrate Subscriber to Group | Admin Portal</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : "xl"}>
                <Typography variant="h3" component="h1" paragraph>
                    Migrate Subscriber to Gym Buddy Group
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                >
                    Moves a standalone subscriber under another user's Gym Buddy
                    plan as a buddy, and <b>cancels the subscriber's own Stripe
                    subscription</b>. Preview first, then confirm. Use Search
                    User to find the userId (sub) for each person.
                </Typography>

                <FormProvider
                    methods={methods}
                    onSubmit={handleSubmit(onPreview)}
                >
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Card sx={{ p: 3 }}>
                                <Stack spacing={3}>
                                    <RHFTextField
                                        name="subscriberUserId"
                                        label="Subscriber userId (sub to move)"
                                        required
                                    />
                                    <RHFTextField
                                        name="ownerUserId"
                                        label="Target group owner userId (sub)"
                                        required
                                    />
                                    <RHFTextField
                                        name="groupId"
                                        label="Group ID (optional override)"
                                    />
                                </Stack>
                                <Stack sx={{ mt: 3 }}>
                                    <LoadingButton
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        loading={isPreviewing}
                                        disabled={!isValid}
                                    >
                                        Preview Migration
                                    </LoadingButton>
                                </Stack>
                            </Card>

                            {preview && (
                                <Card sx={{ p: 3, mt: 3 }}>
                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                        Preview
                                    </Typography>
                                    <Stack
                                        spacing={1}
                                        divider={<Divider flexItem />}
                                    >
                                        <Row
                                            label="Subscriber"
                                            value={`${preview.subscriberEmail} (${preview.subscriberUserId})`}
                                        />
                                        <Row
                                            label="Subscriber's own subs (will cancel)"
                                            value={
                                                preview.ownSubscriptions.length
                                                    ? preview.ownSubscriptions
                                                          .map(
                                                              (s) =>
                                                                  `${s.id} [${s.status}]` +
                                                                  (s.currentPeriodEnd
                                                                      ? ` → ${dayjs
                                                                            .unix(
                                                                                s.currentPeriodEnd
                                                                            )
                                                                            .format(
                                                                                "YYYY-MM-DD"
                                                                            )}`
                                                                      : "")
                                                          )
                                                          .join(", ")
                                                    : "none (nothing to cancel)"
                                            }
                                        />
                                        <Row
                                            label="RevenueCat / IAP active?"
                                            value={
                                                preview.ownRevenueCat
                                                    ? "YES"
                                                    : "no"
                                            }
                                        />
                                        <Row
                                            label="Target owner"
                                            value={`${preview.ownerEmail} (${preview.ownerUserId})`}
                                        />
                                        <Row
                                            label="Target group"
                                            value={`${preview.group.groupId} — ${preview.group.currentSeats}/${preview.group.maxSeats} seats — ${preview.group.status}`}
                                        />
                                    </Stack>

                                    {preview.alreadyMigrated && (
                                        <Alert
                                            severity="info"
                                            sx={{ mt: 2 }}
                                        >
                                            Subscriber is already a member of
                                            this group. Executing will only
                                            ensure their old sub is cancelled.
                                        </Alert>
                                    )}

                                    {preview.blockers.length > 0 && (
                                        <Alert
                                            severity="error"
                                            sx={{ mt: 2 }}
                                        >
                                            <b>Blocked:</b>
                                            <ul style={{ margin: "4px 0 0" }}>
                                                {preview.blockers.map((b) => (
                                                    <li key={b}>{b}</li>
                                                ))}
                                            </ul>
                                        </Alert>
                                    )}

                                    <Stack sx={{ mt: 3 }}>
                                        <LoadingButton
                                            variant="contained"
                                            color="error"
                                            size="large"
                                            loading={isExecuting}
                                            disabled={!preview.canExecute}
                                            onClick={onExecute}
                                        >
                                            Migrate &amp; Cancel Old Sub
                                        </LoadingButton>
                                    </Stack>
                                </Card>
                            )}
                        </Grid>
                    </Grid>
                </FormProvider>
            </Container>
        </>
    );
}

// ----------------------------------------------------------------------

function Row({ label, value }: { label: string; value: string }) {
    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{ py: 0.5 }}
        >
            <Typography variant="subtitle2" color="text.secondary">
                {label}
            </Typography>
            <Typography
                variant="body2"
                sx={{ textAlign: "right", wordBreak: "break-all" }}
            >
                {value}
            </Typography>
        </Stack>
    );
}
